import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import type { BackendModule, ReadCallback, ResourceKey } from 'i18next'

import { setActiveLocale } from '@/lib/locale'
import {
  DEFAULT_UI_LANGUAGE,
  resolveBootstrapLanguage,
  SUPPORTED_UI_LANGUAGES,
} from '@/lib/uiLanguage'

/**
 * Namespaces mirror the product areas, so a screen only pays for the text it uses.
 * Add a new one by dropping `locales/pt-BR/<ns>.json` — the globs below pick it up.
 */
export const I18N_NAMESPACES = [
  'common',
  'auth',
  'settings',
  'chat',
  'crm',
  'assistants',
  'automations',
  'reports',
  'agenda',
  'agency',
  'notifications',
] as const

/**
 * pt-BR is bundled eagerly: it is the fallback language, so it has to resolve synchronously —
 * otherwise a missing key would render raw while the file is still in flight.
 */
const ptResources = import.meta.glob('./locales/pt-BR/*.json', { eager: true }) as Record<
  string,
  { default: ResourceKey }
>

/** en / es load on demand, one namespace at a time. */
const lazyResources = import.meta.glob(['./locales/en/*.json', './locales/es/*.json']) as Record<
  string,
  () => Promise<{ default: ResourceKey }>
>

function bundledPtBr(): Record<string, ResourceKey> {
  const bundle: Record<string, ResourceKey> = {}
  for (const [path, mod] of Object.entries(ptResources)) {
    const ns = path.split('/').pop()?.replace('.json', '')
    if (ns) bundle[ns] = mod.default
  }
  return bundle
}

/**
 * Resolves a namespace file for the non-default languages. A namespace that has not been
 * translated yet resolves to an empty bundle, which makes i18next fall through to pt-BR
 * instead of showing the raw key.
 */
const lazyBackend: BackendModule = {
  type: 'backend',
  init: () => {},
  read: (language: string, namespace: string, callback: ReadCallback) => {
    const loader = lazyResources[`./locales/${language}/${namespace}.json`]
    if (!loader) {
      callback(null, {})
      return
    }

    loader()
      .then((mod) => callback(null, mod.default))
      .catch((error) => callback(error as Error, false))
  },
}

void i18n
  .use(lazyBackend)
  .use(initReactI18next)
  .init({
    resources: {
      'pt-BR': bundledPtBr(),
    },
    // Required when bundled resources and a backend coexist, so i18next still asks the
    // backend for the languages it does not already have in memory.
    partialBundledLanguages: true,
    lng: resolveBootstrapLanguage(),
    fallbackLng: DEFAULT_UI_LANGUAGE,
    supportedLngs: SUPPORTED_UI_LANGUAGES as unknown as string[],
    defaultNS: 'common',
    ns: ['common'],
    interpolation: {
      escapeValue: false,
    },
    // Suspense would force a boundary around every screen; the pt-BR fallback already covers
    // the instant before a namespace lands.
    react: {
      useSuspense: false,
    },
    // Return the pt-BR string (via fallbackLng) instead of the raw key when missing in en/es.
    returnNull: false,
  })

/**
 * Single hook point: date/number formatters and `<html lang>` follow whatever i18next is on,
 * no matter which path changed it (account load, manual switch, logout reset).
 */
function syncLanguageSideEffects(language: string) {
  setActiveLocale(language)
  if (typeof document !== 'undefined') {
    document.documentElement.lang = language
  }
}

syncLanguageSideEffects(i18n.language || DEFAULT_UI_LANGUAGE)
i18n.on('languageChanged', syncLanguageSideEffects)

export default i18n
